<!-- BaseButton.vue - Unified Button Component for Modals -->
<template>
  <button
    :type="type"
    :disabled="disabled || loading"
    :class="buttonClasses"
    @click="$emit('click')"
  >
    <!-- Loading spinner -->
    <component
      v-if="loading"
      :is="LoaderIcon"
      class="animate-spin -ml-1 mr-3 h-5 w-5"
      :class="iconColorClass"
    />

    <!-- Icon -->
    <component
      v-else-if="icon"
      :is="icon"
      :class="[iconSize, iconColorClass]"
      :style="iconOnly ? {} : { marginRight: '0.75rem' }"
    />

    <!-- Text content -->
    <span v-if="!iconOnly">
      {{ loading ? loadingText : text }}
    </span>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Loader2 } from 'lucide-vue-next'

const LoaderIcon = Loader2

export interface Props {
  text?: string
  loadingText?: string
  type?: 'button' | 'submit' | 'reset'
  variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'warning' | 'ghost' | 'outline'
  size?: 'sm' | 'md' | 'lg' | 'xl'
  disabled?: boolean
  loading?: boolean
  icon?: any
  iconOnly?: boolean
  fullWidth?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  text: '',
  loadingText: 'Loading...',
  type: 'button',
  variant: 'primary',
  size: 'md',
  disabled: false,
  loading: false,
  icon: undefined,
  iconOnly: false,
  fullWidth: false
})

defineEmits<{
  click: []
}>()

// Computed classes
const buttonClasses = computed(() => {
  const baseClasses = [
    'inline-flex',
    'justify-center',
    'items-center',
    'font-medium',
    'rounded-xl',
    'transition-all',
    'duration-200',
    'focus:outline-none',
    'focus:ring-2',
    'focus:ring-offset-2',
    'disabled:opacity-50',
    'disabled:cursor-not-allowed',
    'transform',
    'hover:scale-105',
    'active:scale-95'
  ]

  // Size classes
  const sizeClasses = {
    sm: ['px-3', 'py-1.5', 'text-sm'],
    md: ['px-4', 'py-2', 'text-base'],
    lg: ['px-6', 'py-3', 'text-base'],
    xl: ['px-8', 'py-4', 'text-lg']
  }

  // Variant classes
  const variantClasses = {
    primary: [
      'text-white',
      'bg-primary-gradient',
      'hover:shadow-xl',
      'focus:ring-blue-500',
      'shadow-lg'
    ],
    secondary: [
      'text-gray-700',
      'dark:text-gray-200',
      'bg-white',
      'dark:bg-gray-600',
      'border',
      'border-gray-300',
      'dark:border-gray-500',
      'hover:bg-gray-50',
      'dark:hover:bg-gray-500',
      'focus:ring-gray-500',
      'shadow-sm'
    ],
    danger: ['text-white', 'bg-red-gradient', 'hover:shadow-xl', 'focus:ring-red-500', 'shadow-lg'],
    success: [
      'text-white',
      'bg-green-gradient',
      'hover:shadow-xl',
      'focus:ring-green-500',
      'shadow-lg'
    ],
    warning: [
      'text-white',
      'bg-yellow-gradient',
      'hover:shadow-xl',
      'focus:ring-yellow-500',
      'shadow-lg'
    ],
    ghost: [
      'text-gray-700',
      'dark:text-gray-200',
      'hover:bg-gray-100',
      'dark:hover:bg-gray-700',
      'focus:ring-gray-500'
    ],
    outline: [
      'text-blue-600',
      'dark:text-blue-400',
      'border-2',
      'border-blue-600',
      'dark:border-blue-400',
      'hover:bg-blue-50',
      'dark:hover:bg-blue-900/20',
      'focus:ring-blue-500'
    ]
  }

  // Width classes
  const widthClasses = props.fullWidth ? ['w-full'] : []

  // Icon-only classes
  const iconOnlyClasses = props.iconOnly ? ['aspect-square'] : []

  return [
    ...baseClasses,
    ...sizeClasses[props.size],
    ...variantClasses[props.variant],
    ...widthClasses,
    ...iconOnlyClasses
  ]
})

const iconSize = computed(() => {
  const sizes = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-5 w-5',
    xl: 'h-6 w-6'
  }
  return sizes[props.size]
})

const iconColorClass = computed(() => {
  if (props.variant === 'secondary' || props.variant === 'ghost') {
    return 'text-gray-500 dark:text-gray-400'
  }
  if (props.variant === 'outline') {
    return 'text-blue-600 dark:text-blue-400'
  }
  return 'text-white'
})
</script>

<style scoped>
/* Gradient backgrounds */
.bg-primary-gradient {
  background-image: var(--gradient-primary, linear-gradient(135deg, #667eea 0%, #764ba2 100%));
}

.bg-red-gradient {
  background-image: linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%);
}

.bg-green-gradient {
  background-image: linear-gradient(135deg, #51cf66 0%, #40c057 100%);
}

.bg-yellow-gradient {
  background-image: linear-gradient(135deg, #ffd43b 0%, #fab005 100%);
}

/* Enhanced hover effects */
.bg-primary-gradient:hover {
  background-image: linear-gradient(0deg, #0088cc 24%, #26b8ff 110%);
}

.bg-red-gradient:hover {
  background-image: linear-gradient(135deg, #f56565 0%, #e53e3e 100%);
}

.bg-green-gradient:hover {
  background-image: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
}

.bg-yellow-gradient:hover {
  background-image: linear-gradient(135deg, #ecc94b 0%, #d69e2e 100%);
}
</style>
